import { useState, useEffect, useCallback } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useStore } from 'react-redux';

import type { LiveConnectionStats } from 'features/analytics/interfaces';
import { transformMetricsToLiveStats } from 'features/analytics/helpers/';
import { WebRTCCallQualityAnalytics } from 'features/analytics/services/';
import { useAppDispatch } from 'features/app/hooks/';
import type { RootState } from 'features/app/store/store';

export interface WebRTCLiveStatsHook {
  connectionStats: Record<string, LiveConnectionStats>;
  isStatsAvailable: boolean;
}

export const useWebRTCLiveStats = (isActive = true): WebRTCLiveStatsHook => {
  const [connectionStats, setConnectionStats] = useState<
    Record<string, LiveConnectionStats>
  >({});
  const [isStatsAvailable, setIsStatsAvailable] = useState(false);
  const { webrtcStatsReporter } = useFlags();
  const dispatch = useAppDispatch();
  const store = useStore<RootState>();
  const isEnabled = webrtcStatsReporter?.enabled ?? false;

  const updateLiveStats = useCallback(async (): Promise<void> => {
    if (!isEnabled || !isActive) {
      setConnectionStats({});
      setIsStatsAvailable(false);
      return;
    }

    const analytics = WebRTCCallQualityAnalytics.getInstance(
      dispatch,
      store.getState
    );
    if (!analytics) {
      setConnectionStats({});
      setIsStatsAvailable(false);
      return;
    }

    try {
      const connectionIds = analytics.getAllConnections();
      if (connectionIds.length === 0) {
        setConnectionStats({});
        setIsStatsAvailable(false);
        return;
      }

      const newStats: Record<string, LiveConnectionStats> = {};
      let hasValidStats = false;

      for (const peerId of connectionIds) {
        const metrics = analytics.getLatestConnectionMetrics(peerId);
        const rawStats = await analytics.getRawWebRTCStats(peerId);

        if (metrics) {
          newStats[peerId] = await transformMetricsToLiveStats(
            peerId,
            metrics as Record<string, string | number | undefined>,
            rawStats
          );
          hasValidStats = true;
        }
      }

      setConnectionStats(newStats);
      setIsStatsAvailable(hasValidStats);
    } catch (e: unknown) {
      console.error(e);
      setConnectionStats({});
      setIsStatsAvailable(false);
    }
  }, [dispatch, isEnabled, isActive, store]);

  useEffect(() => {
    if (!isEnabled || !isActive) {
      setConnectionStats({});
      setIsStatsAvailable(false);
      return;
    }

    // Initial update
    updateLiveStats();

    const interval = setInterval(updateLiveStats, 2000);

    return () => clearInterval(interval);
  }, [isEnabled, isActive, updateLiveStats]);

  return {
    connectionStats,
    isStatsAvailable,
  };
};
